import { ProjectEstimationSubmission } from './ProjectEstimationSubmission';
import { TeamReviewDecision } from './TeamReviewDecision';

export type ProjectStageStatus = 'AwaitingTeamEstimate' | 'AwaitingTeamApproval' | 'Ready';

export interface CurrentProjectStage {
  stageKey: string;
  stageLabel: string;
  nodeId: string;
  index: number;
  canProceed: boolean;
  stageStatus: ProjectStageStatus;
  displayMessage: string;
}

export interface ProjectStageBundle {
  currentStage: CurrentProjectStage;
  projectEstimation: ProjectEstimationSubmission | null;
}

const DEFAULT_WAITING_MESSAGE = 'Our team is preparing your project estimate. You will be able to continue as soon as it is ready.';
const DEFAULT_APPROVAL_MESSAGE = 'Your estimate is being reviewed by the team before release. You will be able to continue after approval.';

const normalizeDecision = (decision: unknown): TeamReviewDecision => {
  if (typeof decision === 'number') {
    return decision;
  }

  const parsed = Number(decision ?? TeamReviewDecision.NotYetStarted);
  return Number.isNaN(parsed) ? TeamReviewDecision.NotYetStarted : parsed;
};

const isProjectEstimationNode = (enrollment: any): boolean => {
  const enrollmentType = enrollment?.type ?? enrollment?.enrollmentType ?? '';
  return String(enrollmentType).includes('ProjectEstimationEnrollment');
};

const getProjectEstimationSubmission = (userSubmission: any): ProjectEstimationSubmission | null => {
  if (!userSubmission) {
    return null;
  }

  const submissionType = userSubmission.type ?? '';
  const looksLikeEstimation =
    String(submissionType).includes('ProjectEstimation') ||
    userSubmission.billingType === 'Hourly' ||
    userSubmission.billingType === 'Fixed';

  if (!looksLikeEstimation) {
    return null;
  }

  return ProjectEstimationSubmission.fromDto(userSubmission);
};

export const deriveCurrentProjectStage = (
  enrollmentSubmission: any | null,
  statusFallback: number | null = null,
): ProjectStageBundle | null => {
  if (!enrollmentSubmission || !isProjectEstimationNode(enrollmentSubmission.enrollment)) {
    return null;
  }

  const enrollment = enrollmentSubmission.enrollment;
  const requiresTeamReview = Boolean(enrollment?.requiresTeamReview ?? false);
  const estimationSubmission = getProjectEstimationSubmission(enrollmentSubmission.userSubmission);
  const decision = normalizeDecision(enrollmentSubmission.userSubmission?.teamReview?.decision);

  let stageStatus: ProjectStageStatus = 'Ready';
  let canProceed = true;
  let displayMessage = '';
  let projectEstimation: ProjectEstimationSubmission | null = estimationSubmission;

  if (!estimationSubmission) {
    stageStatus = 'AwaitingTeamEstimate';
    canProceed = false;
    displayMessage = DEFAULT_WAITING_MESSAGE;
  } else if (requiresTeamReview && decision !== TeamReviewDecision.Approved) {
    stageStatus = 'AwaitingTeamApproval';
    canProceed = false;
    displayMessage = DEFAULT_APPROVAL_MESSAGE;
    projectEstimation = null;
  }

  if (statusFallback !== null && statusFallback === TeamReviewDecision.WaitingForUserSubmission && !estimationSubmission) {
    stageStatus = 'AwaitingTeamEstimate';
    canProceed = false;
    displayMessage = DEFAULT_WAITING_MESSAGE;
  }

  return {
    currentStage: {
      stageKey: 'projectEstimation',
      stageLabel: 'Project Estimation',
      nodeId: enrollmentSubmission.nodeId ?? '',
      index: Number(enrollmentSubmission?.node?.index ?? enrollmentSubmission?.index ?? 0),
      canProceed,
      stageStatus,
      displayMessage,
    },
    projectEstimation,
  };
};
